/**
 * Pending user input handler
 * Records the agent's question in the conversation so the UI can prompt the user.
 * The answer is sent back via the resume endpoint.
 */
import type {
  AgentMessageResponse,
  JurisdictionSelectPrompt,
} from '@/types/formulation/formulation';
import type { StreamMessageHandler, StreamMessageData, HandlerContext } from '../types';

export const pendingUserInputHandler: StreamMessageHandler = {
  name: 'pending-user-input',

  canHandle(data: StreamMessageData): boolean {
    return data.business_type === 'pending_user_input';
  },

  handle(data: StreamMessageData, ctx: HandlerContext): boolean {
    // ignore any non-completed event.
    if (data.tool_state !== 'completed') return false;

    const { tool_key, content, id } = data;
    const raw = (content as { raw?: JurisdictionSelectPrompt } | undefined)?.raw;
    if (!raw?.prompt || !raw.options?.length) {
      console.warn('[Formulation] pending_user_input missing prompt or options:', data);
      return false;
    }

    // A completed message always carries its persisted id; without one it cannot be
    // deduplicated against the stop_flag refresh.
    if (id === undefined) {
      console.warn('[Formulation] pending_user_input has no id, ignoring:', data);
      return false;
    }

    const { sessionIdValue, streamIteration, roundStartIso, deps } = ctx.streamContext;
    const { conversationData } = deps;

    const message: AgentMessageResponse = {
      id,
      clientKey: String(id),
      business_type: 'pending_user_input',
      session_id: sessionIdValue,
      iteration: streamIteration,
      tool_key,
      content: content as Record<string, unknown> | null,
      tool_state: 'completed',
      gmt_created: roundStartIso,
    };

    // upsert by id so a re-delivered message replaces rather than duplicates.
    const existing = conversationData.value ?? [];
    const idx = existing.findIndex((m) => m.id === id);
    if (idx >= 0) {
      const next = [...existing];
      next[idx] = message;
      conversationData.value = next;
    } else {
      conversationData.value = [...existing, message];
    }

    return false;
  },
};
